"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  initAutoDiscovery,
  getModelRouteSegment,
  listGeneratorConfigs,
  type ModelConfigEntry,
} from "@/client/config/models.config";
import { normalizeLocale } from "@/lib/i18n/locales";
import { cn } from "@/lib/utils";

interface ModelShowcaseScrollProps {
  locale: string;
}

interface ModelShowcaseItem {
  key: string;
  label: string;
  description: string;
  routeSegment: string;
  modelKey: string;
  videoSrc: string;
}

const buildShowcaseItems = (
  configs: ModelConfigEntry[],
  translate: ReturnType<typeof useTranslation>["t"],
): ModelShowcaseItem[] => {
  // listGeneratorConfigs 已经过滤了 visible 和 role，无需再次过滤
  return configs.map((config) => {
      const routeSegment = getModelRouteSegment(config);

      let label = config.label;
      const labelKey = config.i18n?.labelKey;
      if (labelKey) {
        const translated = translate(labelKey, { defaultValue: label });
        if (typeof translated === "string" && translated.trim()) {
          label = translated;
        }
      }

      let description = config.description ?? "";
      const descriptionKey = config.i18n?.descriptionKey;
      if (descriptionKey) {
        const translated = translate(descriptionKey, {
          defaultValue: description,
        });
        if (typeof translated === "string" && translated.trim()) {
          description = translated;
        }
      }

      return {
        key: config.modelKey,
        label,
        description,
        routeSegment,
        modelKey: config.modelKey,
        videoSrc: `/videos/${config.modelKey}-demo.mp4`,
      };
    });
};

const ModelShowcaseScroll: React.FC<ModelShowcaseScrollProps> = ({ locale }) => {
  const { t } = useTranslation("pages");
  const normalizedLocale = useMemo(() => normalizeLocale(locale), [locale]);

  const [items, setItems] = useState<ModelShowcaseItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [direction, setDirection] = useState<"up" | "down" | null>(null);

  const scrollAnimatingRef = useRef(false);
  const wheelTimeoutRef = useRef<number | null>(null);
  const activeIndexRef = useRef(0);

  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      try {
        await initAutoDiscovery();
        if (!mounted) return;
        // 仅使用生成模型（排除 processor 类型）用于首页展示
        const configs = listGeneratorConfigs();
        if (!mounted) return;
        setItems(buildShowcaseItems(configs, t));
      } catch (error) {
        console.error("[ModelShowcaseScroll] 加载模型失败", error);
      }
    };

    void loadItems();

    return () => {
      mounted = false;
    };
  }, [t]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setHasInteracted(true);
    }, 3000);

    return () => {
      window.clearTimeout(timer);
      if (wheelTimeoutRef.current !== null) {
        window.clearTimeout(wheelTimeoutRef.current);
      }
    };
  }, []);

  const scrollToSection = (index: number) => {
    if (index === activeIndexRef.current) return;
    if (index < 0 || index >= items.length) return;
    if (scrollAnimatingRef.current) return;

    const newDirection: "up" | "down" =
      index > activeIndexRef.current ? "down" : "up";
    setDirection(newDirection);
    scrollAnimatingRef.current = true;
    setActiveIndex(index);
    activeIndexRef.current = index;

    window.setTimeout(() => {
      scrollAnimatingRef.current = false;
    }, 600);
  };

  const handleWheel: React.WheelEventHandler<HTMLDivElement> = (event) => {
    if (scrollAnimatingRef.current) return;

    if (wheelTimeoutRef.current !== null) {
      return;
    }

    const deltaY = event.deltaY;
    if (deltaY === 0) return;

    const currentIndex = activeIndexRef.current;

    if (deltaY > 0 && currentIndex < items.length - 1) {
      setHasInteracted(true);
      scrollToSection(currentIndex + 1);
    } else if (deltaY < 0 && currentIndex > 0) {
      setHasInteracted(true);
      scrollToSection(currentIndex - 1);
    }

    wheelTimeoutRef.current = window.setTimeout(() => {
      wheelTimeoutRef.current = null;
    }, 150);
  };

  const handleDotClick = (index: number) => {
    setHasInteracted(true);
    scrollToSection(index);
  };

  if (!items.length) {
    return null;
  }

  const activeItem = items[activeIndex];

  return (
    <div
      className="relative h-screen w-full overflow-hidden bg-background"
      onWheel={handleWheel}
    >
      <AnimatePresence initial={false} mode="sync">
        <motion.section
          key={activeItem.key}
          className={cn(
            "absolute top-0 left-0 flex h-screen w-full flex-col items-center justify-center gap-8 px-6 py-10 md:flex-row md:px-12",
          )}
          initial={
            direction === "down"
              ? { y: "100%" }
              : { y: 0 }
          }
          animate={{ y: 0 }}
          exit={
            direction === "up"
              ? { y: "100%" }
              : { y: 0 }
          }
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{ zIndex: activeIndex + 1 }}
        >
          <div className="flex w-full flex-col items-start justify-center gap-4 md:w-2/5">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              {activeItem.label}
            </h2>
            {activeItem.description ? (
              <p className="max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
                {activeItem.description}
              </p>
            ) : null}

            <Button
              asChild
              size="lg"
              className="mt-2"
            >
              <Link
                href={`/${normalizedLocale}/create?model=${encodeURIComponent(
                  activeItem.routeSegment,
                )}`}
              >
                {t("pages:landing.hero.ctaPrimary", {
                  defaultValue: "开始创作",
                })}
              </Link>
            </Button>
          </div>

          <div className="hidden w-full md:w-3/5 sm:block">
            <div
              className={cn(
                "flex h-64 w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-card md:h-[420px]",
              )}
            >
              <video
                key={activeItem.videoSrc}
                className="h-full w-full object-cover"
                src={activeItem.videoSrc}
                controls
              />
            </div>
          </div>

          {activeIndex === 0 && !hasInteracted && (
            <motion.div
              className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-muted-foreground"
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <ChevronDown className="h-6 w-6" />
              </motion.div>
            </motion.div>
          )}
        </motion.section>
      </AnimatePresence>

      <div className="pointer-events-none absolute right-4 top-1/2 flex -translate-y-1/2 flex-col items-center gap-2">
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleDotClick(index)}
              className={cn(
                "pointer-events-auto h-2.5 w-2.5 rounded-full border border-border bg-background/60 transition-colors",
                isActive && "h-3 w-3 border-primary bg-primary",
              )}
              aria-label={item.label}
              aria-pressed={isActive}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ModelShowcaseScroll;
